import { getPointOfSaleFromLocation } from 'common';
import {
  Cabin,
  BoundFlight,
  TripType,
  AwardCart,
  Passenger,
  PassengerFieldError,
  AwardBaggageAllowance,
  AwardTaxes,
  Coupon,
  AwardOrder,
  City,
  PassengerKey,
  SignedInUser,
  PassengerErrorMap,
  PassengerErrors,
} from 'shared';
import create from 'zustand';
import { persist } from 'zustand/middleware';

import { CepData, CepError, CepErrors } from './constants';
import { getFormattedBoundDates } from './utils';

export enum BookingStatus {
  INITIATED = 'booking-initiated',
  PAYMENT_PENDING = 'payment-pending',
  BOOKING_PENDING = 'booking-pending',
  BOOKING_TIMEOUT = 'booking-timeout',
  SUCCESS = 'booking-success',
  ERROR = 'booking-error',
}

type getLocationFn = (x: string) => City | undefined;

type InitiateArgs = {
  cartId: string;
  cepData: CepData;
  signedInUser: SignedInUser;
  getLocationByIdentifier: getLocationFn;
};

export type Modifiers = {
  initiate: (args: InitiateArgs) => void;
  setPnr: (pnr: string) => void;
  setError: (error: Error) => void;
  setIsTopbarExpanded: (isExpanded: boolean) => void;
  setOutboundFlight: SetSelectedFlight;
  setInboundFlight: SetSelectedFlight;
  setCepData: (cepData: CepData, getLocationByIdentifier: getLocationFn) => void;
  removeCepErrors: () => void;
  removeCartId: () => void;
  setDates: (dates: { outboundDate: string | undefined; inboundDate: string | undefined }) => void;
  updatePassenger: (id: string, passenger: Partial<Passenger>) => void;
  setPassengerFieldError: (id: string, key: PassengerKey, error: PassengerFieldError | undefined) => void;
  setPassengerErrors: (fieldErrors: PassengerErrorMap) => void;
  updateStateFromCart: (cart: AwardCart) => void;
  setStatus: (status: Exclude<BookingStatus, BookingStatus.ERROR>, order?: AwardOrder) => void;
  setCoupons: (coupons: Coupon[]) => void;
  toggleCoupon: (selectedCoupon: Coupon) => void;
  setSessionId: (sessionId: string) => void;
  clear: () => void;
};

export type AwardPassenger = Passenger & { errors?: PassengerErrors };

type PassengerMap = Record<string, AwardPassenger>;

export type BookingState = {
  pos: string;
  signedInUser: SignedInUser;
  cartId: string;
  status: BookingStatus;
  isTopbarExpanded: boolean;
  isCepValid: boolean;
  passengers: PassengerMap;
  cepErrors: CepErrors;
  offerId?: string;
  pnr?: string;
  error?: Error;
  outboundFlight?: BoundFlight;
  outboundCabin?: Cabin;
  inboundFlight?: BoundFlight;
  inboundCabin?: Cabin;
  baggageAllowance?: AwardBaggageAllowance;
  taxes?: AwardTaxes;
  orderId?: string;
  selectedCoupons: Coupon[];
  coupons: Coupon[];
} & CepData;

export type FlightArgs = { offerId: string; flight: BoundFlight; cabin: Cabin } | undefined;
export type SetSelectedFlight = (args: FlightArgs) => void;

function getInitialState(args: InitiateArgs): BookingState {
  const { cartId, cepData, signedInUser, getLocationByIdentifier } = args;

  const { origin, destination, tripType, outboundDate, inboundDate, passengerCounts } = cepData;

  const originCity = origin ? getLocationByIdentifier(origin) : undefined;

  const destinationCity = destination ? getLocationByIdentifier(destination) : undefined;

  const boundDates = getFormattedBoundDates(outboundDate, inboundDate);

  let isCepValid = Boolean(originCity && destinationCity && boundDates?.outboundDate);

  if (tripType === TripType.ROUND_TRIP) {
    isCepValid = isCepValid && Boolean(boundDates?.inboundDate);
  }

  const pos = getPointOfSaleFromLocation(location);

  return {
    status: BookingStatus.INITIATED,
    cartId,
    orderId: '',
    signedInUser,
    pos,
    origin: originCity ? origin : undefined,
    destination: destinationCity ? destination : undefined,
    tripType,
    passengerCounts,
    isCepValid,
    isTopbarExpanded: !isCepValid,
    passengers: {},
    cepErrors: {},
    coupons: [],
    selectedCoupons: [],
    ...boundDates,
  };
}

export type State = { booking: BookingState | undefined } & Modifiers;

const bookingStateMiddleware =
  (set: (updater: (state: State) => Partial<State>) => void) =>
  (fn: (bookingState: BookingState) => Partial<BookingState>) => {
    set(state => {
      if (state.booking) {
        return {
          booking: {
            ...state.booking,
            ...fn(state.booking),
          },
        };
      }

      return state;
    });
  };

export const useBookingStore = create(
  persist<State>(
    set => {
      const setBookingState = bookingStateMiddleware(set);

      return {
        booking: undefined,

        initiate: ({ cartId, cepData, signedInUser, getLocationByIdentifier }) =>
          set(() => ({
            booking: getInitialState({ cartId, cepData, signedInUser, getLocationByIdentifier }),
          })),

        setCepData: (cepData, getLocationByIdentifier) =>
          setBookingState(() => {
            const { origin, destination, tripType, outboundDate, inboundDate, passengerCounts } = cepData;

            const originCity = origin ? getLocationByIdentifier(origin) : undefined;

            const destinationCity = destination ? getLocationByIdentifier(destination) : undefined;

            const boundDates = getFormattedBoundDates(outboundDate, inboundDate);

            let isCepValid = Boolean(originCity && destinationCity && boundDates?.outboundDate);
            if (tripType === TripType.ROUND_TRIP) {
              isCepValid = isCepValid && Boolean(boundDates?.inboundDate);
            }

            if (isCepValid) {
              return {
                isCepValid: true,
                cepErrors: {},
                origin,
                destination,
                tripType,
                passengerCounts,
                ...boundDates,
              };
            }

            const cepErrors: CepErrors = {
              origin: origin ? undefined : CepError.FIELD_REQUIRED,
              destination: destination ? undefined : CepError.FIELD_REQUIRED,
              outboundDate: boundDates?.outboundDate ? undefined : CepError.FIELD_REQUIRED,
            };

            if (tripType === TripType.ROUND_TRIP && !boundDates?.inboundDate) {
              cepErrors.inboundDate = CepError.FIELD_REQUIRED;
            }

            return {
              isCepValid: false,
              cepErrors,
            };
          }),

        removeCepErrors: () => setBookingState(() => ({ cepErrors: {} })),
        removeCartId: () => setBookingState(() => ({ cartId: undefined })),

        setDates: dates =>
          setBookingState(bookingState => {
            const boundDates = getFormattedBoundDates(dates.outboundDate, dates.inboundDate);

            if (!boundDates) {
              return bookingState;
            }

            return resetFlight(boundDates);
          }),

        setError: error =>
          setBookingState(() => ({
            status: BookingStatus.ERROR,
            error,
          })),

        setPnr: pnr =>
          setBookingState(() => ({
            pnr,
          })),

        setOutboundFlight: args =>
          setBookingState(bookingState => {
            const state: Partial<BookingState> = {
              offerId: args?.offerId ?? bookingState.offerId,
              outboundFlight: args?.flight,
              outboundCabin: args?.cabin,
              taxes: undefined,
            };

            if (args && bookingState.offerId !== args.offerId) {
              state.inboundFlight = undefined;
              state.inboundCabin = undefined;
            }

            return state;
          }),

        setInboundFlight: args =>
          setBookingState(bookingState => {
            const state: Partial<BookingState> = {
              offerId: args?.offerId ?? bookingState.offerId,
              inboundFlight: args?.flight,
              inboundCabin: args?.cabin,
              taxes: undefined,
            };

            if (args && bookingState.offerId !== args.offerId) {
              state.outboundFlight = undefined;
              state.outboundCabin = undefined;
            }

            return state;
          }),

        updatePassenger: (id, passenger) =>
          setBookingState(bookingState => {
            return {
              passengers: {
                ...bookingState.passengers,
                [id]: {
                  ...bookingState.passengers[id],
                  ...passenger,
                },
              },
            };
          }),

        setPassengerFieldError: (id, key, error) =>
          setBookingState(bookingState => {
            return {
              passengers: {
                ...bookingState.passengers,
                [id]: {
                  ...bookingState.passengers[id],
                  errors: {
                    ...bookingState.passengers[id].errors,
                    [key]: error,
                  },
                },
              },
            };
          }),

        setPassengerErrors: errors =>
          setBookingState(bookingState => {
            const ids = Object.keys(bookingState.passengers);
            const newPassengerMap = ids.reduce((acc, id) => {
              const passenger = bookingState.passengers[id];
              const passengerFieldErrors = errors[id] ?? {};

              return {
                ...acc,
                [passenger.id]: {
                  ...passenger,
                  errors: passengerFieldErrors,
                },
              };
            }, {} as PassengerMap);

            return {
              ...bookingState,
              passengers: newPassengerMap,
            };
          }),

        updateStateFromCart: cart =>
          setBookingState(bookingState => {
            if (!cart) {
              return bookingState;
            }

            return mapCartToState(bookingState, cart);
          }),

        toggleCoupon: selectedCoupon =>
          setBookingState(bookingState => {
            // TODO - Handle multiple coupons
            return resetFlight({
              ...bookingState,
              selectedCoupons: bookingState.selectedCoupons.find(
                coupon => coupon.couponNumber === selectedCoupon.couponNumber
              )
                ? []
                : [selectedCoupon],
            });
          }),

        setCoupons: (coupons = []) =>
          setBookingState(() => {
            return {
              coupons,
              selectedCouponCodes: coupons.filter(coupon => coupon.applied).map(coupon => coupon.code),
            };
          }),

        setStatus: (status, order) =>
          setBookingState(() => ({
            error: undefined,
            orderId: order?.id,
            pnr: order?.pnr,
            status,
          })),

        setIsTopbarExpanded: isExpanded => setBookingState(() => ({ isTopbarExpanded: isExpanded })),

        setSessionId: sessionId =>
          setBookingState(bookingState => ({ signedInUser: { ...bookingState.signedInUser, sessionId } })),
        clear: () => set(() => ({ booking: undefined })),
      };
    },
    {
      name: 'award-booking-store',
      getStorage: () => sessionStorage,
    }
  )
);

function resetFlight(state?: Partial<BookingState>): Partial<BookingState> {
  return {
    ...state,
    offerId: undefined,
    outboundFlight: undefined,
    outboundCabin: undefined,
    inboundFlight: undefined,
    inboundCabin: undefined,
    baggageAllowance: undefined,
    taxes: undefined,
    orderId: undefined,
  };
}

const getPassengerMap = (passengers: Passenger[]): PassengerMap => {
  return passengers.reduce((acc, passenger) => ({ ...acc, [passenger.id]: passenger }), {});
};

function mapCartToState(state: BookingState, cart: AwardCart): Partial<BookingState> {
  const passengers = cart.passengers ? getPassengerMap(cart.passengers) : state.passengers;

  return {
    cartId: cart.id,
    tripType: cart.tripType,
    offerId: cart.offerId,
    baggageAllowance: cart.baggageAllowance,
    taxes: cart.taxes,
    passengers,
  };
}
